import {
  calculateMargin,
  fetchAccount,
  fetchCompensationRange,
  fetchUsersOnProjectStats,
  updateProject,
} from './account.thunks';
import { isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  accountStoreKey,
  mapDummyMembers,
  groupAccountMembersByMap,
  mapWithContractorMargin,
  sortAccountMembersMap,
  sortGroupedMembers,
  setProjectMargin,
  getNewDummyProject,
  getDefaultDummyMember,
  mapMarignToDummyProject,
  sortAccountDummyMembersMap,
} from './account.const';
import {
  AccountDetail,
  DummyMargin,
  DummyMember,
  SortOrder,
  HeadcountChange,
  HeadcountSummary,
  PutProjectResponseDto,
} from '../../types';

import {
  AccountSortPayloadType,
  AccountState,
  CompensationRangesResponseDto,
  GroupAccountMembersBy,
  MemberField,
  PLEASE_FILL_OUT_ALL_FIELDS_WITH_PROPER_VALUES_ERROR_MESSAGE,
  SortAccountMembersBy,
} from './account.types';
import { DEFAULT_ERROR_MESSAGE } from '../../constants';
import { createSliceCustom } from '../../utils/tests/createSliceCustom';
import { UsersOnProjectStatsResponse, saveOperationalCost } from '../accounts';
import { v4 } from 'uuid';

export const initialState: AccountState = {
  account: null,
  project: null,
  selectedProjectId: null,

  accountMembers: [],
  accountDummyMembers: [],
  accountDummyProjects: [],

  calculatingMarginLoading: null,

  groupBy: GroupAccountMembersBy.Team,
  sortBy: SortAccountMembersBy.Margin,
  sortOrder: SortOrder.Ascending,
  loading: false,
  error: null,
  editableMode: false,
  dateFilterValue: null,
  dateFilterValueLocal: null,
  hideLoading: false,
  editablePingId: null,

  projectedRevenueEndDateIsLoading: false,
  projectedRevenueEndDateError: null,

  userOnProjectStats: null,
  userOnProjectStatsLoading: false,
  userOnProjectStatsError: null,
};

export const returns = createSliceCustom({
  name: accountStoreKey,
  initialState,
  reducers: {
    setAccountDummyMembers: (
      state,
      { payload: dummyMember }: PayloadAction<DummyMember | undefined>,
    ) => {
      if (state.accountDummyMembers && typeof dummyMember !== 'undefined') {
        state.accountDummyMembers.unshift(dummyMember);
        state.accountDummyProjects?.forEach((project) => {
          if (dummyMember?.projectId === project.id) {
            project.membersCount++;
          }
        });
      } else {
        state.accountDummyMembers = [];
        if (state?.account?.projectedRevenue && state?.account?.initialRevenue)
          state.account.projectedRevenue = state?.account?.initialRevenue;
        if (state?.project?.projectedRevenue && state?.project?.initialRevenue)
          state.project.projectedRevenue = state?.project?.initialRevenue;
      }
    },
    updateDummyMemberField: (
      state,
      { payload }: PayloadAction<MemberField>,
    ) => {
      const { fieldName, id, value } = payload;
      if (state.accountDummyMembers?.length) {
        state.accountDummyMembers = state.accountDummyMembers.map(
          (dummyMember) => {
            if (id !== dummyMember.id) return dummyMember;
            return {
              ...dummyMember,
              [fieldName]: value,
            };
          },
        );
      }
      state.editablePingId = v4();
    },
    updateMemberField: (state, { payload }: PayloadAction<MemberField>) => {
      const { fieldName, id, value } = payload;
      if (state.accountMembers?.length) {
        state.accountMembers = state.accountMembers.map((group) => ({
          ...group,
          members: group.members.map((member) => {
            if (member.id !== id || payload.projectId !== group.projectId)
              return member;
            return {
              ...member,
              editable: {
                ...(member.editable as DummyMargin),
                [fieldName]: value,
              },
            };
          }),
        }));
      }
      state.editablePingId = v4();
    },
    toggleHideDummyMember: (state, { payload }) => {
      if (!payload?.id || !state.accountDummyMembers?.length) return;

      state.accountDummyMembers = state.accountDummyMembers.map((member) =>
        member.id === payload.id
          ? { ...member, isHidden: payload.isHidden }
          : member,
      );
      state.editablePingId = v4();
    },
    removeDummyMember: (state, { payload }) => {
      if (!payload?.id || !state.accountDummyMembers?.length) return;

      state.accountDummyMembers = state.accountDummyMembers.filter(
        (member) => member.id !== payload.id,
      );
      state.editablePingId = v4();
    },
    setEditablePingId: (state) => {
      state.editablePingId = v4();
    },

    toggleEditableMode: (state) => {
      state.editableMode = !state.editableMode;
    },
    resetForAccountDetails: (state) => {
      state.editableMode = false;
      state.accountMembers = [];
      state.accountDummyMembers = [];
      state.accountDummyProjects = [];
    },
    hideLoading: (state) => {
      state.hideLoading = true;
    },
    setGroupBy: (
      state,
      { payload: groupBy }: PayloadAction<GroupAccountMembersBy>,
    ) => {
      state.groupBy = groupBy;

      const groupByFn = groupAccountMembersByMap[groupBy];
      const sortMembersFn = sortAccountMembersMap[state.sortBy];
      const sortGroupedMembersFn = sortGroupedMembers[state.sortBy];

      if (state.account?.projects) {
        state.accountMembers = groupByFn(state.account.projects).map(
          (group) => ({
            ...group,
            members: group.members.sort(sortMembersFn),
          }),
        );
        state.accountMembers = state.accountMembers.sort(sortGroupedMembersFn);
      }
    },
    setSortBy: (state, { payload }: PayloadAction<AccountSortPayloadType>) => {
      state.sortBy = payload.sortBy;
      state.sortOrder = payload?.sortOrder ?? SortOrder.Ascending;

      const sortMembersFn = sortAccountMembersMap[state.sortBy];
      const sortGroupedMembersFn = sortGroupedMembers[state.sortBy];

      let accountMembers = state.accountMembers.map((group) => ({
        ...group,
        members:
          state.sortOrder === SortOrder.Ascending
            ? group.members.sort(sortMembersFn)
            : group.members.sort(sortMembersFn).reverse(),
      }));

      accountMembers = accountMembers.sort(sortGroupedMembersFn);

      state.accountMembers =
        state.sortOrder === SortOrder.Ascending
          ? accountMembers
          : accountMembers.reverse();

      if (state.accountDummyMembers?.length) {
        const dummyMemberSortingFn = sortAccountDummyMembersMap[state.sortBy];
        const sortedDummyMembers =
          state.accountDummyMembers.sort(dummyMemberSortingFn);
        state.accountDummyMembers =
          state.sortOrder === SortOrder.Ascending
            ? sortedDummyMembers
            : sortedDummyMembers.reverse();
      }
    },
    setEditableForMembers: (state) => {
      if (!state.account) return;

      const groupByFn = groupAccountMembersByMap[state.groupBy];
      const sortMembersFn = sortAccountMembersMap[state.sortBy];
      const sortGroupedMembersFn = sortGroupedMembers[state.sortBy];

      const projects = state.project ? [state.project] : state.account.projects;
      state.accountMembers = groupByFn(projects).map((group) => ({
        ...group,
        members: group.members
          .map((item) => ({
            ...item,
            editable: {
              netMargin: item.netMargin,
              grossMargin: item.grossMargin,
              compensationRate: item.compensationRate
                ? Math.round(item.compensationRate)
                : item.compensationRate,
              billRate: item.resource?.billRate ?? 0,
              allocation: item.allocation ?? 100,
              isHidden: item.isHidden ?? false,
            },
          }))
          .sort(sortMembersFn),
      }));
      state.accountMembers = state.accountMembers.sort(sortGroupedMembersFn);
      if (
        !(
          state.accountDummyMembers?.length &&
          state.accountDummyProjects?.length
        )
      ) {
        if (state.account.projectedRevenue) {
          state.account.initialRevenue = state.account.projectedRevenue;
        }
        if (state.project?.projectedRevenue) {
          state.project.initialRevenue = state.project.projectedRevenue;
        }
      }
    },
    setSelectedProjectId: (
      state,
      { payload }: PayloadAction<{ projectId: string | null }>,
    ) => {
      state.selectedProjectId = payload?.projectId;
      if (!payload.projectId) state.project = null;
    },
    setDummyProjects: (state) => {
      const project = getNewDummyProject(state.accountDummyProjects);
      const member = getDefaultDummyMember(project.id);
      state.accountDummyProjects?.push(project);
      state.accountDummyMembers?.push(member);
    },
    resetDummyProjects: (state) => {
      state.accountDummyMembers = state.accountDummyMembers?.filter(
        (x) =>
          state.accountDummyProjects?.findIndex((y) => y.id === x.projectId) ===
          -1,
      );
      state.accountDummyProjects = [];
    },

    setDateFilterValueLocal: (
      state,
      { payload }: PayloadAction<string | null>,
    ) => {
      state.dateFilterValueLocal = payload;
    },
    setDateFilterValue: (state) => {
      state.dateFilterValue = state.dateFilterValueLocal;
    },
    cleanDateFilterValues: (state) => {
      state.dateFilterValue = null;
      state.dateFilterValueLocal = null;
    },
    cleanAccountsTableRow: (
      state,
      { payload }: PayloadAction<{ errMessage: string | null }>,
    ) => {
      state.accountMembers = [];
      state.accountListFetchErrorMessage = payload.errMessage;
    },
    updateProjectedHeadcount: (
      state,
      { payload }: PayloadAction<HeadcountChange[]>,
    ) => {
      const validChanges = payload.filter(
        (headcountChange) => !headcountChange.delete,
      );

      const projectedHeadcountStartDate = validChanges.length
        ? validChanges[0].date
        : '';

      const updatedHeadcountSummary: HeadcountSummary = validChanges
        .filter((entry) => entry.date === projectedHeadcountStartDate)
        .reduce(
          (result, entry) => ({
            projectedHeadcount: (result.projectedHeadcount +=
              entry.countChange),
            projectedJoiningHeadcount: (result.projectedJoiningHeadcount +=
              entry.countChange > 0 ? entry.countChange : 0),
            projectedLeavingHeadcount: (result.projectedLeavingHeadcount +=
              entry.countChange < 0 ? entry.countChange : 0),
            projectedHeadcountStartDate,
          }),
          {
            projectedHeadcount: 0,
            projectedJoiningHeadcount: 0,
            projectedLeavingHeadcount: 0,
            projectedHeadcountStartDate,
          },
        );

      if (state.project) {
        state.project.headcountSummary = updatedHeadcountSummary;
      } else if (state.account) {
        state.account.headcountSummary = updatedHeadcountSummary;
      }
    },
    updateAccount: (
      state,
      { payload }: PayloadAction<PutProjectResponseDto>,
    ) => {
      if (!state.account) return;

      let projectedRevenue = 0;
      state.account.projects = state.account.projects.map((project) => {
        if (payload.financialStats[project.id]) {
          projectedRevenue +=
            payload.financialStats[project.id].projectedRevenue;
          return {
            ...project,
            projectedRevenue:
              payload.financialStats[project.id].projectedRevenue,
          };
        }
        projectedRevenue += project.projectedRevenue || 0;
        return project;
      });

      state.account.projectedRevenue = projectedRevenue;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchAccount.fulfilled,
        (state, { payload }: PayloadAction<AccountDetail>) => {
          state.loading = false;
          state.account = payload ?? null;
          state.project =
            payload?.projects?.find(
              ({ id: projectId }) => projectId === state.selectedProjectId,
            ) ?? null;

          const groupByFn = groupAccountMembersByMap[state.groupBy];
          const sortMembersFn = sortAccountMembersMap[state.sortBy];
          const sortGroupedMembersFn = sortGroupedMembers[state.sortBy];

          if (state.project) {
            state.accountMembers = groupByFn([state.project]).map((group) => ({
              ...group,
              members: group.members.sort(sortMembersFn),
            }));
            state.accountMembers =
              state.accountMembers.sort(sortGroupedMembersFn);
          } else if (state.account?.projects) {
            state.accountMembers = groupByFn(state.account.projects).map(
              (group) => ({
                ...group,
                members: group.members.sort(sortMembersFn),
              }),
            );
            state.accountMembers =
              state.accountMembers.sort(sortGroupedMembersFn);
          }
        },
      )
      .addCase(calculateMargin.pending, (state) => {
        state.calculatingMarginLoading = true;
      })
      .addCase(calculateMargin.fulfilled, (state, { payload }) => {
        state.calculatingMarginLoading = false;
        if (!payload) return;

        // Update after account margins are calculated
        if ('projects' in payload) {
          if (!state.account) return;
          state.account = {
            ...state.account,
            grossMargin: payload.grossMargin,
            netMargin: payload.netMargin,
            projectedRevenue: payload.projectedRevenue,
            projectedRevenueLogs: payload.projectedRevenueLogs,
          };
          const contractors = payload.projects.flatMap((project) => {
            const contractorsWithId = project.contractors.map((contractor) => ({
              ...contractor,
              projectId: project.id,
            }));
            return contractorsWithId;
          });

          state.accountMembers = state.accountMembers.map((group) => ({
            ...group,
            ...setProjectMargin(group.projectId, payload.projects),
            members: group.members.map(
              mapWithContractorMargin(contractors, group.projectId ?? ''),
            ),
          }));

          if (state.accountDummyMembers?.length) {
            state.accountDummyMembers = mapDummyMembers(
              state.accountDummyMembers,
              contractors,
            );
          }
          if (state.accountDummyProjects?.length) {
            state.accountDummyProjects = mapMarignToDummyProject(
              state.accountDummyProjects,
              payload.projects,
            );
          }
          // Update after project margins are calculated
        } else if ('contractors' in payload) {
          state.calculatingMarginLoading = false;

          if (!payload || !state.project) return;
          state.project = {
            ...state.project,
            grossMargin: payload.grossMargin,
            netMargin: payload.netMargin,
            projectedRevenue: payload.projectedRevenue,
          };

          if (state.accountDummyMembers?.length) {
            state.accountDummyMembers = mapDummyMembers(
              state.accountDummyMembers,
              payload.contractors,
            );
          }
          state.accountMembers = state.accountMembers.map((group) => ({
            ...group,
            members: group.members.map(
              mapWithContractorMargin(payload.contractors),
            ),
          }));
        }
      })
      .addCase(calculateMargin.rejected, (state) => {
        state.calculatingMarginLoading = false;

        state.error = {
          message: DEFAULT_ERROR_MESSAGE,
          name: DEFAULT_ERROR_MESSAGE,
        };
      })

      .addCase(
        fetchCompensationRange.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<{
            data: CompensationRangesResponseDto;
            memberId: string;
          }>,
        ) => {
          if (state.accountDummyMembers?.length) {
            state.accountDummyMembers = state.accountDummyMembers.map(
              (dummyMember) => {
                if (dummyMember.id !== payload.memberId) return dummyMember;
                return {
                  ...dummyMember,
                  compensationRate: Math.floor(
                    ((payload.data.compensationRange?.min ?? 0) +
                      (payload.data.compensationRange?.max ?? 0)) /
                      2,
                  ),
                  payRange: [
                    payload.data.compensationRange?.min ?? 0,
                    payload.data.compensationRange?.max ?? 0,
                  ],
                  billRate: payload.data.billRate?.billRate ?? 0,
                  rateCard: payload.data.billRate?.billRate ?? 0,
                };
              },
            );
          }
        },
      )

      .addCase(updateProject.pending, (state) => {
        state.projectedRevenueEndDateIsLoading = true;
        state.projectedRevenueEndDateError = null;
      })

      .addCase(updateProject.fulfilled, (state, { payload }) => {
        if (!state.project) return;
        state.project.projectedRevenueEndDate =
          payload?.project.projectedRevenueEndDate;
        state.project.projectedRevenue =
          payload?.financialStats.projectedRevenue ?? 0;
        state.projectedRevenueEndDateIsLoading = false;
      })

      .addCase(updateProject.rejected, (state) => {
        state.projectedRevenueEndDateIsLoading = false;
        state.projectedRevenueEndDateError = new Error(DEFAULT_ERROR_MESSAGE);
      })

      // Users on project stats reducers
      .addCase(fetchUsersOnProjectStats.pending, (state) => {
        state.userOnProjectStatsLoading = true;
        state.userOnProjectStatsError = null;
      })
      .addCase(
        fetchUsersOnProjectStats.fulfilled,
        (state, { payload }: PayloadAction<UsersOnProjectStatsResponse>) => {
          state.userOnProjectStats = payload;
          state.userOnProjectStatsLoading = false;
          state.userOnProjectStatsError = null;
        },
      )
      .addCase(fetchUsersOnProjectStats.rejected, (state) => {
        state.userOnProjectStatsLoading = false;
        state.userOnProjectStatsError = new Error(
          'Failed to fetch users on project stats.',
        );
      })
      .addCase(saveOperationalCost.fulfilled, (state, { payload }) => {
        if (!payload || !state.account) return;

        if (!state.account?.operationalCost) {
          state.account.operationalCost = [payload];
          return;
        }

        const updatedOperationalCostIndex =
          state.account.operationalCost?.findIndex(
            (operationalCost) => operationalCost.period === payload.period,
          );

        if (!updatedOperationalCostIndex) {
          state.account.operationalCost?.push(payload);
          return;
        }

        state.account.operationalCost[updatedOperationalCostIndex] = payload;
      })

      .addMatcher(isAnyOf(fetchAccount.pending), (state) => {
        state.loading = !state.hideLoading;
        state.error = initialState.error;
      })
      .addMatcher(isAnyOf(fetchAccount.rejected), (state) => {
        state.loading = false;
        state.calculatingMarginLoading = false;
        state.error = {
          message: DEFAULT_ERROR_MESSAGE,
          name: DEFAULT_ERROR_MESSAGE,
        };
      });
  },
});

export const accountSlice = returns.slice;

export const {
  setAccountDummyMembers,
  toggleEditableMode,
  resetForAccountDetails,
  toggleHideDummyMember,
  setEditableForMembers,
  setGroupBy,
  setSortBy,
  updateDummyMemberField,
  updateMemberField,
  setSelectedProjectId,
  setDummyProjects,
  resetDummyProjects,
  hideLoading,
  cleanAccountsTableRow,
  updateProjectedHeadcount,
  updateAccount,
  removeDummyMember,
  setEditablePingId,
} = accountSlice.actions;

export const accountReducers = returns.reducers;
export const accountExtraReducers = returns.extraReducers;
